/* The labs in three languages. A lab is written in Chinese; ?lang=en or ?lang=ja shows it in English or Japanese.

   Each lab keeps its translations next to it, in i18n.json:
     { "把一根倒立的杆撑住": { "en": "Hold an upside-down pole up", "ja": "逆さの棒を立たせておく" },
       "第 {0} 轮":           { "en": "iteration {0}",               "ja": "第 {0} 回" } }
   Static text in index.html is swapped by localizePage() (every text node, title and aria-label whose whole
   trimmed text is a key); text main.ts builds goes through tr("第 {0} 轮", n). Missing keys stay Chinese and
   are listed by window.I18N_MISSING, which the renderer and lint check. */
import { q } from "./stage";

export type Dict = Record<string, { en?: string; ja?: string }>;
export const LANG: "zh" | "en" | "ja" = (["en", "ja"].includes(q.get("lang") || "") ? q.get("lang") : "zh") as "zh" | "en" | "ja";

let dict: Dict = {};
const missing = new Set<string>();
declare global { interface Window { I18N_MISSING: string[] } }
window.I18N_MISSING = [];

const CJK = /[\u3400-\u9fff]/;

/* The string in the page's language; {0}, {1} … are filled from args (numbers are formatted by the caller). */
export function tr(zh: string, ...args: (string | number)[]): string {
  let out = zh;
  if (LANG !== "zh") {
    const t = dict[zh]?.[LANG];
    if (t) out = t;
    else if (CJK.test(zh) && !missing.has(zh)) {
      missing.add(zh);
      window.I18N_MISSING = [...missing];
    }
  }
  return out.replace(/\{(\d+)\}/g, (_, i) => String(args[Number(i)] ?? ""));
}

/* Translate the static page: text nodes, <title>, and title / aria-label / placeholder attributes. */
export function localizePage(d: Dict): void {
  dict = d;
  document.documentElement.lang = { zh: "zh-CN", en: "en", ja: "ja" }[LANG];
  if (LANG === "zh") return;
  if (LANG === "ja") document.documentElement.style.setProperty("--sans", "var(--sans-ja)");
  document.title = swap(document.title);
  // a sentence with markup inside (a coloured phrase): the element is marked data-i18n and its whole
  // innerHTML is the key; the translation carries the same markup
  for (const el of document.querySelectorAll<HTMLElement>("[data-i18n]")) el.innerHTML = swap(el.innerHTML.trim());
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const text = n.nodeValue || "";
    if (CJK.test(text) && !n.parentElement?.closest("[data-i18n]")) n.nodeValue = text.replace(text.trim(), swap(text.trim()));
  }
  for (const el of document.querySelectorAll<HTMLElement>("[title], [aria-label], [placeholder]")) {
    for (const a of ["title", "aria-label", "placeholder"]) {
      const v = el.getAttribute(a);
      if (v && CJK.test(v)) el.setAttribute(a, swap(v));
    }
  }
}

function swap(s: string): string {
  return CJK.test(s) ? tr(s) : s;
}
